import { useSWR } from '@tiger/swr';
import { observer } from 'mobx-react';
import { Navigate, useSearchParams } from 'react-router-dom';
import Card from 'components/Card';
import ErrorBox from 'components/ErrorBox';
import Skeleton from 'components/Skeleton';
import { useCustomerInfo } from 'hooks/useCustomerInfo';
import APP from 'stores/APP';
import security from 'stores/security';
import { IdTypeEnum } from 'types/customer';
import API from 'utils/API';
import classlist from 'utils/classlist';
import AccountItem from './components/AccountItem';
import StatusIcon from './components/StatusIcon';
import { getUpdateInfoList } from './helper';
import { EditTypeEnum, ProcessStatusEnum } from './types';
import './style.scss';

const Account: React.FC = observer(() => {
    const { currentCustomerId: customerId } = security;
    const [searchParams] = useSearchParams();
    const isDarkMode = searchParams.get('skin') === '1' && APP.isTiger;
    const {
        data: dataStatus,
        error: errorStatus,
        isLoading: isLoadingStatus,
        mutate: mutateStatus
    } = useSWR<Record<EditTypeEnum, { modify_status: ProcessStatusEnum | null }>>(
        {
            url: API.CUSTOMER.account.modifyStatus(),
            params: {
                application_type: 'edit',
                customer_id: customerId
            }
        },
        {
            dataChain: res => res.data?.modify_process
        }
    );
    const {
        data: dataCustomerInfo,
        error: errorCustomerInfo,
        isLoading: isLoadingCustomerInfo,
        mutate: mutateCustomerInfo
    } = useCustomerInfo(customerId);

    const finalError = (!dataStatus && errorStatus) || (!dataCustomerInfo && errorCustomerInfo);
    const { id_type, has_delivery_address } = dataCustomerInfo || {};
    const updateInfoList = getUpdateInfoList(id_type === IdTypeEnum.SSN_IDS, !!has_delivery_address);

    if (!customerId) {
        return <Navigate to="../" />;
    }

    return (
        <Card
            page
            className={classlist('account-root', {
                'dark-skin': isDarkMode
            })}>
            <div className="list-group-container">
                <h3 className="list-card-title">{__('基本信息')}</h3>
                {isLoadingStatus || isLoadingCustomerInfo ? (
                    <Skeleton line={5} />
                ) : finalError ? (
                    <ErrorBox
                        error={finalError}
                        onClick={() => {
                            errorStatus && mutateStatus();
                            errorCustomerInfo && mutateCustomerInfo();
                        }}
                    />
                ) : (
                    updateInfoList.map(({ type, path, title, eventName }, index) => {
                        const status = dataStatus?.[type]?.modify_status;
                        const isPending = status === ProcessStatusEnum.PENDING;

                        return (
                            <AccountItem
                                key={index}
                                title={title}
                                isDarkMode={isDarkMode}
                                btnText={__('修改')}
                                onClick={() => APP.updateHref(`/accounts/update/${customerId}/${path}`, APP.isTiger)}
                                statusIcon={
                                    status &&
                                    [ProcessStatusEnum.PENDING, ProcessStatusEnum.REJECTED].includes(status) ? (
                                        <StatusIcon
                                            status={status}
                                            isDarkMode={isDarkMode}
                                            statusText={isPending ? __('审核中') : __('重新提交')}
                                        />
                                    ) : undefined
                                }
                                disabledLink={isPending}
                                eventParams={{
                                    eventType: '账户设置',
                                    eventName
                                }}
                                isLink
                            />
                        );
                    })
                )}
            </div>
        </Card>
    );
});

export default Account;
